/* eslint-disable @typescript-eslint/no-explicit-any */
// Imports for external libraries go here.
import { FC, useContext, useEffect } from 'react';
import {
  Config,
  EditableComponent,
  MappedComponentProperties,
  ResponsiveGrid,
} from '@adobe/aem-react-editable-components';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { OfferCardCarousel } from '@marriott/mi-offers-components';
import { canUseDOM } from '@marriott/shared/mi-helper-utils';
// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.

import { experienceFragmentComponentTypes, merchandisingContentID } from '../../modules/utils/constants';
import { PageParamsContext } from '../../modules/context/PageContext';
import { useServiceStore, serviceStore } from '../../modules/store/serviceStore';
import { LocalServices } from '../LocalServices';
import { LetsConnect } from '../LetsConnect';
import { PropertyPortalExperienceFragment } from '../PropertyPortalExperienceFragment';
import { PropertyPortalContainerProps } from './PropertyPortalContainer.types';
import { StyledPropertyPortalContainer } from './PropertyPortalContainer.styles';
import { ConnectContainer } from '../ConnectContainer';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { DCADecisionContainer } from '@marriott/mi-merch-components';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { respGridUtil } from '@marriott/mi-headless-utils';

const { NEXT_PUBLIC_AEM_SITE } = process.env;

// Use named rather than default exports.
export const PropertyPortalContainer: FC<PropertyPortalContainerProps> = (pageProps: PropertyPortalContainerProps) => {
  const { preProcessorResponse, requestId } = useContext(PageParamsContext);
  const { headersData, locale } = preProcessorResponse || {};
  const setServiceData = useServiceStore((state: serviceStore) => state.setservice);

  //Method for returning Responsive Grid for showing parsys in Author mode
  const PropertyPortalContainer = () => {
    const resourceType = `mi-aem-ppv5-spa/components/content/experiencefragment`;

    return (
      <div data-testid="property-portal-container-component">
        <ResponsiveGrid
          gridClassNames={`col-12`}
          columnClassNames={{
            responsivegrid: `col-12`,
          }}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          pagePath={pageProps?.pagePath}
          itemPath={`${pageProps?.itemPath}/container-0`}
          config={{
            isEmpty: () => true,
            resourceType: resourceType,
          }}
        />
      </div>
    );
  };

  //Method for returning components to be rendering at end point URL
  const PropertyPortalContainerWrapper = (cardName: string, jsonData: any, contentID: string | undefined) => {
    const returnable: Array<any> = [];
    const dcadecisioncontainer = 'mi-aem-common-spa/components/content/dcadecisioncontainer';
    // eslint-disable-next-line no-prototype-builtins
    if (jsonData?.hasOwnProperty(cardName)) {
      const card = jsonData[cardName];
      const cardItems = card[':items'];
      for (const itemKey in cardItems) {
        const item = cardItems[itemKey];
        const type = cardItems?.[itemKey]?.[':type'];
        const referencePath = cardItems?.[itemKey]?.['referencePath'];
        if (type === experienceFragmentComponentTypes?.experiencefragment) {
          returnable.push(
            <PropertyPortalExperienceFragment
              {...item}
              key={itemKey}
              contentID={contentID}
              isLightning={pageProps?.isLightning ?? false}
            />
          );
          // eslint-disable-next-line no-prototype-builtins
        } else if (type === experienceFragmentComponentTypes?.letsConnect) {
          returnable.push(<LetsConnect {...item} key={itemKey} />);
        } else if (type === experienceFragmentComponentTypes?.staticwrapper && !referencePath) {
          returnable.push(<PropertyPortalExperienceFragment {...item} key={`${item?.id}`} contentID={contentID} />);
        } else if (type === experienceFragmentComponentTypes?.serviceCarousel) {
          returnable.push(
            <LocalServices {...item} key={itemKey} contentID={contentID} isAuthorMode={pageProps?.isAuthorMode} />
          );
        } else if (type === experienceFragmentComponentTypes?.offers) {
          const obj = {
            model: item,
          };
          returnable.push(
            <OfferCardCarousel
              {...obj}
              key={itemKey}
              offersData={item?.productSpecificResponse ?? item?.fallbackOfferIdsList ?? []}
              isAuthorMode={pageProps?.isAuthorMode}
              acceptLanguage={locale ?? 'en-US'}
              requestId={requestId}
              cookie={canUseDOM ? document?.cookie : headersData.cookie}
            />
          );
        } else if (type == dcadecisioncontainer) {
          const gridUtil = respGridUtil({ ...item });
          returnable.push(
            <div className="overflow-hidden">
              <DCADecisionContainer {...gridUtil} model={{ ...gridUtil }} key={itemKey} />
            </div>
          );
        } else if (cardItems?.[itemKey]) {
          returnable.push(<ConnectContainer {...item} key={itemKey} contentID={contentID} />);
        }
      }
      return <div data-testid="brand-container-component-wrapper">{returnable}</div>;
    }
    return null;
  };

  useEffect(() => {
    if (typeof window !== 'undefined' && pageProps?.isAuthorMode) {
      const headLinks = document.head.getElementsByTagName('link');
      const headLinksArr = Array.from(headLinks);
      const rtlGlobalStyleLink = headLinksArr.find(pre => pre.href.includes('/marriot.global.rtl.css'));
      rtlGlobalStyleLink?.parentNode?.removeChild(rtlGlobalStyleLink);
    }
  }, [pageProps?.isAuthorMode]);

  useEffect(() => {
    if (pageProps?.contentID === merchandisingContentID) {
      setServiceData(
        {
          merchandisingAvailable: true,
        },
        false,
        false,
        {}
      );
    }
  }, [pageProps?.contentID, setServiceData]);

  const showPropertyPortalContainer =
    pageProps.isAuthorMode && pageProps?.isInEditor ? (
      <div>{Array.from({ length: 1 }, _ => PropertyPortalContainer())}</div>
    ) : (
      <div className={`${pageProps?.styleclass ?? ''}`}>
        {pageProps?.cqItemsOrder &&
          PropertyPortalContainerWrapper(pageProps?.cqItemsOrder?.[0], pageProps?.cqItems, pageProps?.contentID)}
      </div>
    );

  return (
    <StyledPropertyPortalContainer data-component-name="o-ppv5-brandedcontainer" className="m-container-fullbleed">
      <div className={pageProps?.styleclass}>{showPropertyPortalContainer}</div>
    </StyledPropertyPortalContainer>
  );
};

export const PropertyPortalConfig = {
  emptyLabel: 'PropertyPortalContainerComponent',
  isEmpty: (_props: MappedComponentProperties) => false,
  resourceType: `${NEXT_PUBLIC_AEM_SITE}/components/content/brandedcontainer`,
} as Config<MappedComponentProperties>;

export const PropertyPortalContainerEditable = (props: PropertyPortalContainerProps) => (
  <EditableComponent config={PropertyPortalConfig} {...props}>
    <PropertyPortalContainer {...props} />
  </EditableComponent>
);
